import type { Rng } from '../../core/rng';
import { layoutToWorld } from '../../core/coords';
import type { Vec3 } from '../../geo/types';
import { FLAME_COLOUR } from '../../render/night/nightLights';
import type { NightLight } from '../types';
import { painterScaleOf, type PlacedShip } from './shipPlacement';
import { SHIP_TEMPLATES, type ShipType } from './shipTemplates';

/*
 * A light on about every other big ship after dark (docs/NIGHT.md): the lantern at its stern
 * (shipLantern.ts), where the watch on board would keep one. Which ships show one is an estimate;
 * the open boats and the small craft lie dark.
 */
export const SHIP_LIGHTS = {
  types: ['kraeck', 'hulk', 'bark'] as readonly ShipType[],
  share: 0.5,
  reach: 9,
  glow: 0.9,
} as const;

/** A ship that shows a lantern: its place in the fleet and how far its flicker runs ahead. */
export interface LitShip {
  readonly ship: PlacedShip;
  readonly index: number;
  readonly phase: number;
}

/** The ships that show a lantern after dark, in the fleet's order. */
export function litShips(ships: readonly PlacedShip[], rng: Rng): LitShip[] {
  return ships.flatMap((ship, index): LitShip[] => {
    if (!SHIP_LIGHTS.types.includes(ship.type) || ship.painterAnchor !== 'origin') return [];
    if (!SHIP_TEMPLATES[ship.type].form?.aftcastle.length) return [];
    const lit = rng.fork(ship.id);
    if (lit.next() >= SHIP_LIGHTS.share) return [];
    return [{ ship, index, phase: lit.range(0, Math.PI * 2) }];
  });
}

/** The light of a lit ship's lantern, its candle burning at `position` in the world. */
export function shipLight(lit: LitShip, position: Vec3): NightLight {
  return {
    position,
    colour: FLAME_COLOUR,
    reach: SHIP_LIGHTS.reach,
    size: SHIP_LIGHTS.glow,
    phase: lit.phase,
    rides: { origin: layoutToWorld(lit.ship.position, 0), scale: painterScaleOf(lit.ship.type) },
  };
}
